import { PageTransitionButtonContainer, PageTransitionContainer } from 'components/animations';
import { AppBar, Body, Button, PageLayout, Spinner } from 'components/common';
import { usePageTransition } from 'hooks/animations';
import useUnderage from 'hooks/useUnderage';
import React, { useCallback, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { handleCancelAndClose } from 'utils/underageUtils';

const PageUnderAgeForm: React.FC = () => {
  const { capturedFile, idCardFile, setIdCardFile, createdAt, isUploading, setUploading, reset, uploadImage, isFromVerification, setFromVerification } =
    useUnderage();
  const animation = usePageTransition();
  const location = useLocation();
  // state 없음 = 수동 모드 — PicturePreview→Form 복귀가 state 없이 온다.
  const isYoti = (location.state as { mode?: string } | null)?.mode === 'yoti';
  const buttonRef = useRef<HTMLDivElement>(null);
  const toastShownRef = useRef(false);
  // unmount 후 늦게 도착하는 FileChooser 네이티브 콜백 차단용.
  const mountedRef = useRef(true);
  const hMainContainer = document.getElementById('hMainContainer');

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    const hMainContainer = document.getElementById('hMainContainer');
    if (hMainContainer && !toastShownRef.current && isFromVerification) {
      CommonUI.showToast(hMainContainer, { text: brsc.age_verification_toast, duration: 2000, position: 'top' });
      toastShownRef.current = true;
      setFromVerification(false);
    }

    if (!capturedFile && !isYoti) {
      Transition.showPage(Def.PAGE_UNDERAGE_VERIFICATION);
    } else if (!isYoti && capturedFile && idCardFile) {
      // yoti 폴백에서 이미 업로드한 신분증(같은 createdAt 파일) 재사용 — 재제출 없이 미리보기로 직행.
      Transition.showPage(Def.PAGE_UNDERAGE_PICTURE_PREVIEW);
    } else {
      setUploading(false);
    }

    return () => {
      window.FaceDetector?.setFaceDetect(0, false, () => {});
    };
  }, [capturedFile, idCardFile, setUploading, isFromVerification, setFromVerification, isYoti]);

  const fileSelected = useCallback(
    async (image: string) => {
      if (!image || isUploading || !mountedRef.current) return;

      const base64Data = image.startsWith('data:') ? image : `data:image/jpeg;base64,${image}`;
      setIdCardFile({ base64: base64Data, timestamp: Date.now() });

      try {
        setUploading(true);
        const filename = `idcard_${createdAt}.jpg`;
        await uploadImage(base64Data, filename, 'idcard');

        if (isYoti) {
          const pre = await YotiAvs.precheck(createdAt);
          if (!mountedRef.current) return;
          if (pre.ok && pre.result === 'id') {
            const s = await YotiAvs.createSession();
            if (!mountedRef.current) return;
            if (s.ok) {
              Transition.showPage(Def.PAGE_UNDERAGE_YOTI_VERIFY, { sessionId: s.sessionId, sdkId: s.sdkId });
              return;
            }
          }
          Transition.showPage(Def.PAGE_UNDERAGE_VERIFICATION);
          return;
        }

        Transition.showPage(Def.PAGE_UNDERAGE_PICTURE_PREVIEW);
      } catch (error) {
        if (hMainContainer) {
          CommonUI.showToast(hMainContainer, { text: brsc.try_later, duration: 2000, position: 'top' });
        }
      } finally {
        setUploading(false);
      }
    },
    [isUploading, createdAt, uploadImage, setIdCardFile, setUploading, hMainContainer, isYoti],
  );

  useEffect(() => {
    if (!buttonRef.current || !window.FileChooser?.setActor) return;
    window.FileChooser.setActor(buttonRef.current, fileSelected, null, 0.7, 1000);
  }, [fileSelected]);

  const handleSubmit = () => {
    if (!window.FileChooser?.btnClicked || !buttonRef.current) {
      throw new Error('Error: FileChooser is not available');
    }
    window.FileChooser.btnClicked({ currentTarget: buttonRef.current });
  };

  const handleBack = () => {
    if (isYoti) {
      Transition.showPage(Def.PAGE_UNDERAGE_YOTI_PREPARE);
      return;
    }
    handleCancelAndClose(() => {
      animation.resetAnimation?.();
      reset();
    });
  };

  return (
    <PageLayout>
      <AppBar title={brsc.age_verification_title_7} leadingIcon="icon/ic_close.svg" onBack={handleBack} />
      <Body className="flex flex-col pb-[120px]">
        <PageTransitionContainer isVisible={animation.contentVisible} className="flex-1 px-6 pb-[60px] pt-6">
          <div className="flex h-full flex-col items-center justify-start">
            <img src="/imgweb/underage/id_card.png" alt="underage" className="w-full" />
            <div className="my-4 flex flex-col gap-3">
              <div className="typography-title-md text-base-txt-secondary">{brsc.age_verification_title_8}</div>
              <div>
                <div className="typography-body-sm text-base-txt-secondary">{brsc.age_verification_contents_11}</div>
                <div className="typography-body-sm text-base-txt-secondary">{brsc.age_verification_contents_12}</div>
                <div className="typography-body-sm text-base-txt-secondary">{brsc.age_verification_contents_13}</div>
              </div>
              <div className="typography-body-sm text-base-txt-secondary">{brsc.age_verification_contents_14}</div>
            </div>
          </div>
        </PageTransitionContainer>

        <PageTransitionButtonContainer isVisible={animation.buttonVisible}>
          <div ref={buttonRef}>
            <Button
              style={{
                text: isUploading ? '' : brsc.age_verification_btn,
                color: 'btn-bg-secondary',
                fontColor: 'btn-txt-secondary',
                trailing: isUploading ? <Spinner /> : undefined,
              }}
              onClick={isUploading ? undefined : handleSubmit}
              className="w-full"
            />
          </div>
        </PageTransitionButtonContainer>
      </Body>
    </PageLayout>
  );
};

export default PageUnderAgeForm;
